import { Link } from 'react-router-dom';
import { Section, Stat, Table, useApi } from './kit';

// Run-13 (2026-11-02): the ads path to paying. Ads were invisible from Home ("Connect Reddit Ads" chips that vanished
// once connected) and from Reports (only the outbound funnel). Both read the Ad campaigns overview
// (/portal/ad-campaigns, src/adengine/client/report.ts); this file holds the Home card and the Reports tab.

type Platform = 'reddit' | 'meta' | 'x' | 'linkedin';
type Slim = {
  platforms: Array<{ platform: Platform; name: string; available: boolean; connection: { status: string; mode: 'own' | 'managed' } | null; caps: { daily_cap_usd: number; monthly_cap_usd: number } | null;
    spend: { today_usd: number; month_usd: number } }>;
  wallet?: { balance_usd: number };
  best_ad: { headline: string; set_id: string; channel: Platform; impressions: number; clicks: number; spend_usd: number; ctr: number } | null;
  test_sets: Array<{ id: string; angle: string; channel: Platform; status: string; ads: number; running: number; spend_usd: number; impressions: number; clicks: number; conversions: number }>;
};

const usd = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const NAME: Record<Platform, string> = { reddit: 'Reddit', meta: 'Meta', x: 'X', linkedin: 'LinkedIn' };

/** Home: the ads at a glance (wallet, spend against the caps, the best ad), and a Run ads entry that stays after connecting. */
export function AdsCard() {
  const q = useApi<Slim>('/portal/ad-campaigns');
  const o = q.data;
  if (!o) return null; // Home works without it
  const live = o.platforms.filter((p) => p.available && p.connection && p.connection.status === 'active');
  const managed = o.platforms.some((p) => p.connection?.mode === 'managed');
  const today = live.reduce((n, p) => n + p.spend.today_usd, 0);
  const cap = live.reduce((n, p) => n + (p.caps?.daily_cap_usd ?? 0), 0);
  const month = live.reduce((n, p) => n + p.spend.month_usd, 0);
  const monthCap = live.reduce((n, p) => n + (p.caps?.monthly_cap_usd ?? 0), 0);
  const running = o.test_sets.filter((t) => t.running > 0).length;
  const started = live.length > 0 || o.test_sets.length > 0;
  const best = o.best_ad;
  return (
    <section className="section card-refer" aria-label="Ads">
      <span className="jicon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11v2a1 1 0 0 0 1 1h3l6 4V6L7 10H4a1 1 0 0 0-1 1zM17 9a4 4 0 0 1 0 6" /></svg></span>
      <div>
        <b>Ads</b>
        {started ? (
          <span className="muted small">
            {live.length ? <>Spent {usd(today)} today{cap ? ` of ${usd(cap)} a day` : ' (no caps set: nothing spends)'}, {usd(month)} this month{monthCap ? ` of ${usd(monthCap)}` : ''}. </> : 'No ad account connected yet. '}
            {managed ? <>Ad wallet {usd(o.wallet?.balance_usd ?? 0)} (<Link to="/billing#ad-wallet">top up</Link>). </> : null}
            {running ? `${running} test set${running === 1 ? '' : 's'} running. ` : o.test_sets.length ? 'No test set running. ' : ''}
            {best ? <>Best ad: “{best.headline}” on {NAME[best.channel]}, {best.clicks} click{best.clicks === 1 ? '' : 's'} ({(best.ctr * 100).toFixed(1)}%). </> : o.test_sets.length ? 'No clicks yet; numbers arrive over the first hours. ' : ''}
          </span>
        ) : <span className="muted small">Outlevo writes ads from the facts on your site and tests them free before you spend a cent. Connect Reddit, Meta, X or LinkedIn, or let Outlevo run it for you.</span>}
      </div>
      <span className="row">
        {best && best.clicks > 0 ? <Link className="btn" to={`/ad-campaigns/${best.set_id}`}>Let Outlevo scale this</Link> : null}
        <Link className="btn btn-primary" to="/ad-campaigns">Run ads</Link>
      </span>
    </section>
  );
}

/** Reports → Ads: what the ad test sets spent and brought, by set and in total. */
export function AdsReport() {
  const q = useApi<Slim>('/portal/ad-campaigns');
  if (q.isLoading) return <p className="muted" role="status">Loading…</p>;
  const sets = q.data?.test_sets ?? [];
  if (!sets.length) return <Section title="Ads"><p className="muted">No ad test sets yet. <Link to="/ad-campaigns">Run ads</Link> and their spend, clicks and conversions appear here.</p></Section>;
  const sum = (k: 'spend_usd' | 'impressions' | 'clicks' | 'conversions') => sets.reduce((n, t) => n + t[k], 0);
  const clicks = sum('clicks');
  return (
    <Section title="Ads">
      <div className="grid">
        <Stat label="Spent" value={usd(sum('spend_usd'))} note={`${sets.length} test set${sets.length === 1 ? '' : 's'}`} />
        <Stat label="Impressions" value={sum('impressions').toLocaleString('en-US')} />
        <Stat label="Clicks" value={clicks.toLocaleString('en-US')} note={clicks ? `${usd(sum('spend_usd') / clicks)} a click` : 'none yet'} />
        <Stat label="Conversions" value={sum('conversions').toLocaleString('en-US')} note={sum('conversions') ? `${usd(sum('spend_usd') / sum('conversions'))} each` : undefined} />
      </div>
      <Table caption="Ad test sets" rows={sets} rowLink={(t) => `/ad-campaigns/${t.id}`} columns={[{ key: 'angle', label: 'Test set' }, { key: 'channel', label: 'Platform', render: (t) => NAME[t.channel] }, 'status',
        { key: 'spend_usd', label: 'Spent', render: (t) => usd(t.spend_usd) }, { key: 'impressions', label: 'Impressions' }, { key: 'clicks', label: 'Clicks' },
        { key: 'cpc', label: 'Per click', render: (t) => (t.clicks ? usd(t.spend_usd / t.clicks) : '—') }, { key: 'conversions', label: 'Conversions' }]} />
    </Section>
  );
}
